import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VisionStatement = () => {
  const [activeVision, setActiveVision] = useState('future');

  const visionAspects = {
    future: {
      title: 'Future Goals',
      icon: 'Telescope',
      description: 'Our ambitious roadmap for the next decade',
      content: {
        mainVision: 'To become the global leader in ethical AI transformation, empowering 1,000+ enterprises worldwide to harness artificial intelligence for sustainable growth and positive societal impact.',
        keyGoals: [
          {
            icon: 'Globe',
            title: 'Global Expansion',
            description: 'Establish presence in 15+ countries with localized AI expertise',
            timeline: '2025-2027'
          },
          {
            icon: 'Users',
            title: 'Team Growth',
            description: 'Scale to 200+ AI specialists, researchers, and consultants',
            timeline: '2024-2026'
          },
          {
            icon: 'Lightbulb',
            title: 'Innovation Labs',
            description: 'Launch 5 specialized AI research and development centers',
            timeline: '2025-2028'
          },
          {
            icon: 'Award',
            title: 'Industry Leadership',
            description: 'Establish thought leadership in ethical AI and responsible innovation',
            timeline: 'Ongoing'
          }
        ]
      }
    },
    market: {
      title: 'Market Positioning',
      icon: 'Target',
      description: 'How we see ourselves in the evolving AI landscape',
      content: {
        mainVision: 'To be recognized as the most trusted AI consulting partner, known for delivering transformative solutions that balance technological advancement with ethical responsibility and business value.',
        keyGoals: [
          {
            icon: 'Shield',
            title: 'Ethical AI Leadership',
            description: 'Pioneer industry standards for responsible AI development and deployment',
            timeline: 'Industry Standard'
          },
          {
            icon: 'Zap',
            title: 'Innovation Excellence',
            description: 'Lead breakthrough research in practical AI applications for enterprise',
            timeline: 'Continuous Innovation'
          },
          {
            icon: 'Handshake',
            title: 'Partnership Ecosystem',
            description: 'Build comprehensive network of technology and consulting partners',
            timeline: 'Strategic Growth'
          },
          {
            icon: 'TrendingUp',
            title: 'Market Impact',
            description: 'Drive measurable business transformation across multiple industries',
            timeline: 'Proven Results'
          }
        ]
      }
    },
    technology: {
      title: 'Technology Vision',
      icon: 'Cpu',
      description: 'Our commitment to advancing AI capabilities',
      content: {
        mainVision: 'To pioneer the next generation of AI technologies that are not only powerful and efficient but also transparent, explainable, and aligned with human values and business objectives.',
        keyGoals: [
          {
            icon: 'Brain',
            title: 'Advanced AI Research',
            description: 'Develop proprietary AI models optimized for enterprise applications',
            timeline: 'R&D Focus'
          },
          {
            icon: 'Eye',
            title: 'Explainable AI',
            description: 'Create AI systems that provide clear reasoning and decision transparency',
            timeline: 'Core Principle'
          },
          {
            icon: 'Lock',
            title: 'Secure AI',
            description: 'Integrate cybersecurity principles into all AI solution architectures',
            timeline: 'Built-in Security'
          },
          {
            icon: 'Workflow',
            title: 'AI Integration',
            description: 'Seamlessly integrate AI into existing business processes and systems',
            timeline: 'Implementation Focus'
          }
        ]
      }
    }
  };

  const currentVision = visionAspects[activeVision];

  const impactMetrics = [
    {
      target: '1,000+',
      label: 'Enterprises Transformed',
      description: 'Global businesses empowered by our AI solutions',
      icon: 'Building2'
    },
    {
      target: '$10B+',
      label: 'Client Value Generated',
      description: 'Measurable business value created through AI',
      icon: 'DollarSign'
    },
    {
      target: '50+',
      label: 'Countries Served',
      description: 'Global reach with local expertise',
      icon: 'Globe'
    },
    {
      target: '100%',
      label: 'Ethical AI Commitment',
      description: 'Responsible AI practices in every solution',
      icon: 'Heart'
    }
  ];

  return (
    <section>
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-foreground mb-4">Vision Statement</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Our vision for the future of AI consulting and the positive impact we aim to create
        </p>
      </div>

      {/* Vision Aspects */}
      <div className="bg-white rounded-2xl shadow-soft border border-border overflow-hidden mb-16">
        {/* Vision Navigation */}
        <div className="border-b border-border">
          <div className="flex">
            {Object.entries(visionAspects)?.map(([key, aspect]) => (
              <button
                key={key}
                onClick={() => setActiveVision(key)}
                className={`flex-1 px-6 py-4 text-center font-medium transition-smooth ${
                  activeVision === key
                    ? 'bg-primary text-white' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Icon name={aspect?.icon} size={18} />
                  <span className="hidden sm:inline">{aspect?.title}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Vision Content */}
        <div className="p-8 lg:p-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full gradient-primary flex items-center justify-center">
              <Icon name={currentVision?.icon} size={32} color="white" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-4">{currentVision?.title}</h3>
            <p className="text-muted-foreground mb-6">{currentVision?.description}</p>
          </div>

          {/* Main Vision Statement */}
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-6 lg:p-8 mb-8">
            <blockquote className="text-lg text-center text-foreground leading-relaxed italic">
              "{currentVision?.content?.mainVision}"
            </blockquote>
          </div>

          {/* Key Goals */}
          <div className="grid lg:grid-cols-2 gap-6">
            {currentVision?.content?.keyGoals?.map((goal, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 rounded-lg hover:bg-muted/30 transition-smooth">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name={goal?.icon} size={16} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-foreground">{goal?.title}</h4>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                      {goal?.timeline}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{goal?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Impact Targets */}
      <div className="mb-16">
        <h3 className="text-2xl font-bold text-foreground text-center mb-8">2030 Impact Targets</h3>
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6">
          {impactMetrics?.map((metric, index) => (
            <div key={index} className="text-center group">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-accent/20 to-accent/10 flex items-center justify-center group-hover:scale-110 transition-smooth">
                <Icon name={metric?.icon} size={24} className="text-accent" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">{metric?.target}</div>
              <h4 className="font-semibold text-foreground mb-2">{metric?.label}</h4>
              <p className="text-sm text-muted-foreground">{metric?.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Call to Action */}
      <div className="gradient-primary text-white rounded-2xl p-8 lg:p-12 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/20 flex items-center justify-center">
            <Icon name="Rocket" size={32} color="white" />
          </div>
          
          <h3 className="text-3xl font-bold mb-4">Be Part of the AI Future</h3>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Join us on our mission to transform businesses and create positive impact through responsible AI innovation. 
            Together, we can build a future where artificial intelligence serves humanity's greatest ambitions.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Button 
              variant="secondary"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => window.location.href = '/contact-consultation-request'}
              className="bg-white text-primary hover:bg-white/90"
            >
              Start Your AI Journey
            </Button>
            <Button 
              variant="outline"
              size="lg"
              iconName="Users"
              iconPosition="left"
              onClick={() => window.location.href = '/team-profiles'}
              className="border-white/30 text-white hover:bg-white/10"
            >
              Meet Our Visionaries
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionStatement;